import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { BookOpen, Search, Menu, Building2, GraduationCap, Globe, MapPin } from 'lucide-react';

const YaleUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Yale University";

    return (
        <div className="min-h-screen bg-white font-serif text-[#222] selection:bg-[#00356b] selection:text-white">
            {/* Top Navigation */}
            <nav className="bg-white px-8 py-6 border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-1">
                        <span className="font-serif text-4xl tracking-tighter text-[#00356b] font-normal">{schoolName.split(' ')[0]}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans text-sm font-bold text-[#222]">
                        <a href="#" className="hover:text-[#00356b]">About {schoolName.split(' ')[0]}</a>
                        <a href="#" className="hover:text-[#00356b]">Admissions</a>
                        <a href="#" className="hover:text-[#00356b]">Academics</a>
                        <a href="#" className="hover:text-[#00356b]">Research</a>
                        <a href="#" className="hover:text-[#00356b]">Collections</a>
                        <a href="#" className="hover:text-[#00356b]">Campus Life</a>
                    </div>

                    <div className="flex items-center gap-6">
                        <Search size={22} className="text-[#00356b] cursor-pointer" />
                        <Menu size={22} className="text-[#00356b] cursor-pointer lg:hidden" />
                    </div>
                </div>
            </nav>

            {/* Hero Section - Old Campus vibe */}
            <header className="relative bg-[#f9f9f9] py-20 px-8">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                         <h1 className="text-5xl md:text-7xl font-normal leading-[1.1] text-[#00356b] mb-8">
                             Light and Truth. <br/>
                             <span className="text-[#222] text-4xl md:text-5xl italic block mt-2">Lux et Veritas.</span>
                         </h1>
                         <p className="text-xl text-gray-600 leading-relaxed font-sans mb-10 max-w-lg">
                             Since 1701, Yale has been dedicated to expanding and sharing knowledge, inspiring innovation, and preserving cultural and scientific information for future generations.
                         </p>
                         <div className="flex flex-wrap gap-4">
                             <Button className="bg-[#00356b] hover:bg-[#002040] text-white font-sans font-bold px-8 h-12 rounded-sm text-sm tracking-wide">
                                 Visit {schoolName.split(' ')[0]}
                             </Button>
                             <Button variant="outline" className="border-[#00356b] text-[#00356b] hover:bg-[#ebf2f8] font-sans font-bold px-8 h-12 rounded-sm text-sm tracking-wide bg-transparent">
                                 Apply Now
                             </Button>
                         </div>
                    </div>
                    
                    <div className="relative">
                         {/* Architectural Image Collage */}
                         <div className="aspect-[4/5] bg-gray-200 relative overflow-hidden rounded-t-[200px] border-4 border-white shadow-xl">
                             <img src="https://images.unsplash.com/photo-1592398516087-01048b11163e?q=80&w=2072&auto=format&fit=crop" className="w-full h-full object-cover" alt="Harkness Tower" />
                             <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#00356b]/90"></div>
                             <div className="absolute bottom-8 left-8 text-white">
                                 <span className="font-sans font-bold uppercase tracking-widest text-xs mb-2 block text-[#fff]">Campus Icon</span>
                                 <h2 className="text-2xl font-normal">Harkness Tower</h2>
                             </div>
                         </div>
                    </div>
                </div>
            </header>

            {/* News Grid */}
            <section className="py-24 px-8 max-w-[1400px] mx-auto">
                <h2 className="text-3xl font-serif text-[#00356b] mb-12 flex items-center gap-4">
                    {schoolName} News <span className="h-px bg-gray-200 flex-1"></span>
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                     {[
                         { cat: "Science", title: "Planetary scientists propose new explanation for Mars's 'spiders'", img: "https://images.unsplash.com/photo-1614726365723-49cfae92782f?q=80&w=2069&auto=format&fit=crop" },
                         { cat: "Humanities", title: "Beinecke Library acquires rare manuscripts from 15th century", img: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=2070&auto=format&fit=crop" },
                         { cat: "Health", title: "Yale School of Medicine launches center for RNA biology", img: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2080&auto=format&fit=crop" }
                     ].map((item, i) => (
                         <div key={i} className="group cursor-pointer">
                             <div className="mb-6 overflow-hidden">
                                 <img src={item.img} className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105" alt="News" />
                             </div>
                             <span className="font-sans text-xs font-bold uppercase text-[#00356b] mb-3 block">{item.cat}</span>
                             <h3 className="text-xl font-bold leading-tight group-hover:text-[#00356b] transition-colors font-sans">{item.title}</h3>
                         </div>
                     ))}
                </div>
            </section>

            {/* Factoids */}
            <section className="bg-[#00356b] text-white py-24 px-8">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center">
                    <div>
                        <div className="text-5xl font-serif mb-2">14</div>
                        <div className="font-sans text-sm font-bold opacity-80 uppercase tracking-widest">Residential Colleges</div>
                    </div>
                    <div>
                        <div className="text-5xl font-serif mb-2">6:1</div>
                        <div className="font-sans text-sm font-bold opacity-80 uppercase tracking-widest">Student-Faculty Ratio</div>
                    </div>
                    <div>
                        <div className="text-5xl font-serif mb-2">80+</div>
                        <div className="font-sans text-sm font-bold opacity-80 uppercase tracking-widest">Majors</div>
                    </div>
                    <div>
                        <div className="text-5xl font-serif mb-2">323</div>
                        <div className="font-sans text-sm font-bold opacity-80 uppercase tracking-widest">Years of History</div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#f9f9f9] text-[#222] py-20 px-8 border-t border-gray-200">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1 md:col-span-2">
                        <span className="font-serif text-3xl text-[#00356b] block mb-6">{schoolName}</span>
                        <div className="font-sans text-sm text-gray-600 space-y-1">
                            <p>New Haven, Connecticut 06520</p>
                            <p>203-432-4771</p>
                        </div>
                        <div className="flex gap-4 mt-8">
                             <a href="#" className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-[#00356b] hover:text-white hover:border-[#00356b] transition-colors"><Globe size={18}/></a>
                             <a href="#" className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-[#00356b] hover:text-white hover:border-[#00356b] transition-colors"><MapPin size={18}/></a>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-sans font-bold text-[#00356b] uppercase tracking-widest text-xs mb-6">Audience</h4>
                        <ul className="space-y-3 font-sans text-sm text-gray-600">
                            <li><a href="#" className="hover:text-[#00356b]">Alumni</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Faculty & Staff</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Parents</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Students</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-sans font-bold text-[#00356b] uppercase tracking-widest text-xs mb-6">About</h4>
                        <ul className="space-y-3 font-sans text-sm text-gray-600">
                            <li><a href="#" className="hover:text-[#00356b]">Visiting</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Jobs</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Giving to {schoolName.split(' ')[0]}</a></li>
                            <li><a href="#" className="hover:text-[#00356b]">Privacy Policy</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-16 pt-8 border-t border-gray-200 text-center font-sans text-xs text-gray-500">
                    Copyright © 2026 {schoolName} · All rights reserved
                </div>
            </footer>
        </div>
    );
};

export default YaleUniversity;
